"use client";
import {
  PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { Feature, FeatureControl, FeatureControlUser as User } from "./types";

type FeatureControlContextValue = {
  fc: FeatureControl;
  user?: User;
};

const FeatureControlContext = createContext<
  FeatureControlContextValue | undefined
>(undefined);

export function FeatureControlProvider(
  props: PropsWithChildren<{
    featureControl: FeatureControl;
    user?: User;
  }>
) {
  const { children, featureControl, user } = props;
  const [fc, setFc] = useState<FeatureControl>(createProxy(featureControl));

  /**
   * This logic is to inject the feature control from local storage.
   * The convention is to use `fc-` prefix for the key.
   * Then the `fc-` prefix will be removed and the rest of the key will be used as the feature key.
   * Why useEffect? Because `window` is only defined inside the browser. Will cause an error on the server side.
   */
  useEffect(() => {
    const fc: FeatureControl = {};
    Array.from(
      { length: window.localStorage.length },
      (_, index) => window.localStorage.key(index) as string
    ).forEach((key) => {
      if (key.startsWith("fc-")) {
        const enabled = window.localStorage[key] === "true";
        const fcKey = key.replace(/^fc-/, "");

        fc[fcKey] = fc[fcKey] ?? {};
        fc[fcKey].enabled = enabled;
      }
    });

    if (Object.keys(fc).length) {
      setFc((prev) => createProxy({ ...prev, ...fc }));
    }
  }, []);

  const value = useMemo<FeatureControlContextValue>(
    () => ({ fc, user }),
    [fc, user]
  );

  return (
    <FeatureControlContext.Provider value={value}>
      {children}
    </FeatureControlContext.Provider>
  );
}

export function useGlobalFeatureControl() {
  const value = useContext(FeatureControlContext);

  if (value === undefined) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "`FeatureControlContext` could not find any provider. Falling back to a disabled default."
      );
    }
    return createProxy({});
  }

  return value.fc;
}

export function useFeatureControl<T = unknown>(featureKey: string) {
  return useGlobalFeatureControl()[featureKey] as Feature<T>;
}

export function useFeatureControlUser() {
  const value = useContext(FeatureControlContext);

  if (value === undefined) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "`FeatureControlContext` could not find any provider. Falling back to a disabled default."
      );
    }
    return undefined;
  }

  return value.user;
}

// Feature Control
const defaultFc = { enabled: false, properties: {} };
function createProxy(value: FeatureControl) {
  return new Proxy(value, {
    get(obj, key: string) {
      return obj[key] ?? defaultFc;
    },
  });
}
